<template>
    <div class="animated fadeIn pricing-page">
        <b-row>
            <b-col cols="12">
                <component-header
                    title="Pricing &amp; Fare Engine"
                    :hasSearch="false"
                    :hasCreate="false"
                    :hasReload="false"
                    :hasExport="true"
                    exportButtonText="Export Configuration"
                    @onExport="exportConfig"
                />
            </b-col>
        </b-row>

        <!-- SERVICE MODE -->
        <b-row>
            <b-col cols="12">
                <b-card class="shadow-sm mb-2 service-mode-card">
                    <b-row align-v="center" class="service-mode-row">
                        <b-col cols="12" class="service-mode-col">
                            <span class="service-mode-label"
                                >Service Mode:</span
                            >
                            <b-button
                                type="button"
                                variant="success"
                                size="sm"
                                class="service-mode-btn"
                                disabled
                            >
                                Ride / Taxi Service
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>

        <!-- TABS -->
        <b-tabs v-model="tabIndex" content-class="mt-2" class="pricing-tabs">
            <b-tab title="Pricing Rules">
                <div class="pricing-header-row">
                    <span class="pricing-title">Pricing Rules</span>
                    <b-button
                        variant="success"
                        size="sm"
                        class="add-rule-btn"
                        @click="openAdd"
                    >
                        <i class="fa fa-plus mr-2" aria-hidden="true"></i>
                        Add Pricing Rule
                    </b-button>
                </div>

                <div class="rule-grid">
                    <b-card
                        v-for="rule in pricingRules"
                        :key="rule.key"
                        class="rule-card shadow-sm"
                    >
                        <div class="rule-card-top">
                            <div>
                                <div class="rule-title">{{ rule.title }}</div>
                                <div class="rule-sub">{{ rule.sub }}</div>
                            </div>

                            <div class="rule-icon" aria-hidden="true">
                                <i class="fa fa-car"></i>
                            </div>
                        </div>

                        <div class="rule-value">
                            <span class="currency">₹</span>{{ rule.amount
                            }}<span v-if="rule.suffix" class="suffix">{{
                                rule.suffix
                            }}</span>
                        </div>

                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            block
                            class="rule-edit-btn"
                            @click="openEdit(rule)"
                        >
                            Edit Rule
                        </b-button>
                    </b-card>
                </div>

                <!-- FORMULA -->
                <b-card class="shadow-sm mt-2">
                    <div class="formula-title">
                        Ride Fare Calculation Formula
                    </div>

                    <div class="formula-body">
                        <div class="formula-mono">
                            <div>
                                Total Fare = Base Fare + (Distance × Per KM) +
                                (Time × Per Minute) + Waiting Charge + Booking
                                Fee
                            </div>
                            <div class="formula-bullets">
                                <div>+ Surge Multiplier (if applicable)</div>
                                <div>+ Weather Multiplier (if applicable)</div>
                            </div>
                        </div>

                        <div class="example-box">
                            <div class="example-title">
                                Example Calculation (Indiranagar to
                                Koramangala):
                            </div>
                            <div class="example-lines">
                                <div>Base Fare: ₹40</div>
                                <div>Distance (8 km × ₹12): ₹96</div>
                                <div>Time (22 min × ₹2): ₹44</div>
                                <div>Booking Fee: ₹10</div>
                                <div class="example-total">Total: ₹190</div>
                                <div class="example-note">
                                    * Excluding surge and weather multipliers
                                </div>
                            </div>
                        </div>
                    </div>
                </b-card>
            </b-tab>

            <b-tab title="Surge Pricing">
                <b-card class="shadow-sm surge-card">
                    <div class="surge-header-row">
                        <span class="surge-title">
                            Active Surge Zones - <span class="surge-city">Bangalore</span>
                        </span>
                        <b-button
                            variant="success"
                            size="sm"
                            class="create-surge-btn"
                            @click="openSurgeCreate"
                        >
                            <i
                                class="fa fa-plus mr-2"
                                aria-hidden="true"
                            ></i>
                            Create Surge Rule
                        </b-button>
                    </div>

                    <b-table
                        :items="surgeRules"
                        :fields="surgeFields"
                        responsive
                        stacked="md"
                        bordered
                        thead-class="header"
                        show-empty
                        empty-text="No surge rules found."
                        class="surge-table tableRadius professional-stacked-table"
                    >
                        <template #cell(zone)="{ item }">
                            <span class="zone-cell">
                                <i
                                    class="fa fa-map-marker zone-icon"
                                    aria-hidden="true"
                                ></i>
                                <span class="zone-name">{{ item.zone }}</span>
                            </span>
                        </template>
                        <template #cell(timePeriod)="{ item }">
                            <span class="time-cell">
                                <i
                                    class="fa fa-clock-o time-icon"
                                    aria-hidden="true"
                                ></i>
                                {{ item.timePeriod }}
                            </span>
                        </template>
                        <template #cell(serviceType)="{ item }">
                            <b-badge
                                :class="[
                                    'service-pill',
                                    serviceTypeClass(item.serviceType),
                                ]"
                                variant="light"
                            >
                                {{ item.serviceType }}
                            </b-badge>
                        </template>
                        <template #cell(multiplier)="{ item }">
                            <span class="multiplier">{{
                                formatMultiplier(item.multiplier)
                            }}</span>
                        </template>
                        <template #cell(actions)="{ index }">
                            <div class="inline_btnTable">
                                <b-button
                                    size="sm"
                                    class="button-circle btn-orange"
                                    variant="primary"
                                    @click="openSurgeEdit(index)"
                                >
                                    <i class="fa fa-pencil edit"></i>
                                </b-button>
                                <b-button
                                    size="sm"
                                    class="button-circle"
                                    variant="danger"
                                    @click="deleteSurgeRule(index)"
                                >
                                    <i class="fa fa-trash"></i>
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                </b-card>

                <b-card class="shadow-sm mt-2">
                    <div class="settings-title">Dynamic Surge Settings</div>

                    <b-row class="settings-grid">
                        <b-col md="6">
                            <b-form-group
                                label="High Demand Threshold"
                                label-class="setting-label"
                            >
                                <b-form-input
                                    type="number"
                                    v-model.number="
                                        surgeSettings.highDemandThreshold
                                    "
                                    class="setting-input"
                                />
                                <div class="setting-help">
                                    Trigger surge when demand exceeds this
                                    percentage
                                </div>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group
                                label="Maximum Surge Multiplier"
                                label-class="setting-label"
                            >
                                <b-form-input
                                    type="number"
                                    step="0.1"
                                    v-model.number="surgeSettings.maxMultiplier"
                                    class="setting-input"
                                />
                                <div class="setting-help">
                                    Cap on automatic surge pricing
                                </div>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-button
                        variant="success"
                        size="sm"
                        class="save-settings-btn"
                        @click="saveSurgeSettings"
                    >
                        Save Surge Settings
                    </b-button>
                </b-card>
            </b-tab>

            <b-tab title="Weather-Based">
                <b-card class="shadow-sm">
                    <div class="weather-header-row">
                        <span class="weather-title">Weather-Based Pricing Rules</span>
                        <b-button
                            variant="success"
                            size="sm"
                            class="add-weather-btn"
                            @click="openWeatherCreate"
                        >
                            <i
                                class="fa fa-plus mr-2"
                                aria-hidden="true"
                            ></i>
                            Add Weather Rule
                        </b-button>
                    </div>

                    <div class="weather-list">
                        <div
                            v-for="(rule, idx) in weatherRules"
                            :key="rule.id"
                            class="weather-item"
                        >
                            <div class="weather-left">
                                <div
                                    class="weather-icon"
                                    :class="`weather-${rule.kind}`"
                                    aria-hidden="true"
                                >
                                    <i
                                        class="fa"
                                        :class="weatherIcon(rule.kind)"
                                    ></i>
                                </div>
                                <div class="weather-text">
                                    <div class="weather-name">
                                        {{ rule.title }}
                                    </div>
                                    <div class="weather-desc">
                                        {{ weatherSubtitle(rule) }}
                                    </div>
                                </div>
                            </div>

                            <div class="weather-right">
                                <span
                                    class="weather-status"
                                    :class="
                                        rule.active
                                            ? 'is-active'
                                            : 'is-inactive'
                                    "
                                >
                                    {{ rule.active ? "Active" : "Inactive" }}
                                </span>
                                <b-button
                                    size="sm"
                                    class="button-circle btn-orange"
                                    variant="primary"
                                    @click="openWeatherEdit(idx)"
                                >
                                    <i class="fa fa-pencil edit"></i>
                                </b-button>
                            </div>
                        </div>

                        <div
                            v-if="weatherRules.length === 0"
                            class="weather-empty"
                        >
                            No weather rules found.
                        </div>
                    </div>
                </b-card>
            </b-tab>
        </b-tabs>

        <!-- EDIT PRICING RULE MODAL -->
        <b-modal
            id="edit-pricing-modal"
            v-model="editOpen"
            title="Edit Pricing Rule"
            hide-footer
            @hide="closeEdit"
        >
            <p class="modal-subtitle">Update the title, subtitle and value.</p>
            <b-form-group label="Title" label-for="editTitle">
                <b-form-input
                    id="editTitle"
                    v-model="editTitle"
                    placeholder="e.g. Base Fare"
                />
            </b-form-group>
            <b-form-group label="Subtitle" label-for="editSub">
                <b-form-input
                    id="editSub"
                    v-model="editSub"
                    placeholder="e.g. Initial charge per trip"
                />
            </b-form-group>
            <b-form-group label="Value" label-for="editAmount">
                <b-input-group>
                    <b-input-group-prepend>
                        <b-input-group-text>₹</b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                        id="editAmount"
                        type="number"
                        v-model.number="editAmount"
                        placeholder="e.g. 40"
                    />
                    <b-form-input
                        v-model="editSuffix"
                        placeholder="suffix (optional)"
                    />
                </b-input-group>
            </b-form-group>
            <div class="modal-actions">
                <b-button variant="secondary" @click="closeEdit"
                    >Cancel</b-button
                >
                <b-button variant="dark" @click="saveEdit">Save</b-button>
            </div>
        </b-modal>

        <!-- ADD PRICING RULE MODAL -->
        <b-modal
            id="add-pricing-modal"
            v-model="addOpen"
            title="Add Pricing Rule"
            hide-footer
            @hide="closeAdd"
        >
            <p class="modal-subtitle">
                This will add a new card to the Pricing Rules grid.
            </p>
            <b-form-group label="Title" label-for="addTitle">
                <b-form-input
                    id="addTitle"
                    v-model="newRuleTitle"
                    placeholder="e.g. Night Charge"
                />
            </b-form-group>
            <b-form-group label="Subtitle" label-for="addSub">
                <b-form-input
                    id="addSub"
                    v-model="newRuleSub"
                    placeholder="e.g. Applied after 10 PM"
                />
            </b-form-group>
            <b-form-group label="Value" label-for="addAmount">
                <b-input-group>
                    <b-input-group-prepend>
                        <b-input-group-text>₹</b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                        id="addAmount"
                        type="number"
                        v-model.number="newRuleAmount"
                        placeholder="e.g. 25"
                    />
                    <b-form-input
                        v-model="newRuleSuffix"
                        placeholder="suffix (optional)"
                    />
                </b-input-group>
            </b-form-group>
            <div class="modal-actions">
                <b-button variant="secondary" @click="closeAdd"
                    >Cancel</b-button
                >
                <b-button variant="dark" @click="saveAdd">Add</b-button>
            </div>
        </b-modal>

        <!-- CREATE/EDIT SURGE RULE MODAL -->
        <b-modal
            id="surge-rule-modal"
            v-model="surgeModalOpen"
            :title="
                surgeEditIndex >= 0
                    ? 'Edit Surge Pricing Rule'
                    : 'Create Surge Pricing Rule'
            "
            hide-footer
            @hide="closeSurgeModal"
        >
            <p class="modal-subtitle">
                Configure surge pricing for specific zones and time periods
            </p>
            <b-form-group label="Zone / Area" label-for="surgeZone">
                <b-form-input
                    id="surgeZone"
                    v-model="surgeForm.zone"
                    placeholder="e.g., Koramangala, MG Road"
                />
            </b-form-group>
            <b-form-group label="Time Period" label-for="surgeTime">
                <b-form-input
                    id="surgeTime"
                    v-model="surgeForm.timePeriod"
                    placeholder="e.g., Peak Hours (8-10 AM)"
                />
            </b-form-group>
            <b-form-group label="Service Type" label-for="surgeService">
                <b-form-select
                    id="surgeService"
                    v-model="surgeForm.serviceType"
                    :options="[
                        { value: 'Both', text: 'Both (Ride & Parcel)' },
                        { value: 'Ride', text: 'Ride' },
                        { value: 'Parcel', text: 'Parcel' },
                    ]"
                />
            </b-form-group>
            <b-form-group label="Surge Multiplier" label-for="surgeMultiplier">
                <b-form-input
                    id="surgeMultiplier"
                    type="number"
                    step="0.1"
                    v-model.number="surgeForm.multiplier"
                    placeholder="e.g., 1.8"
                />
            </b-form-group>
            <b-button
                variant="dark"
                block
                class="surge-submit-btn"
                @click="saveSurgeRule"
            >
                {{ surgeEditIndex >= 0 ? "Save Changes" : "Create Surge Rule" }}
            </b-button>
        </b-modal>

        <!-- CREATE/EDIT WEATHER RULE MODAL -->
        <b-modal
            id="weather-rule-modal"
            v-model="weatherModalOpen"
            :title="
                weatherEditIndex >= 0 ? 'Edit Weather Rule' : 'Add Weather Rule'
            "
            size="lg"
            hide-footer
            @hide="closeWeatherModal"
        >
            <p class="modal-subtitle">
                Configure the trigger, multiplier and service applicability.
            </p>
            <b-row class="modal-grid">
                <b-col md="6">
                    <b-form-group label="Weather Type" label-for="weatherType">
                        <b-form-select
                            id="weatherType"
                            v-model="weatherForm.kind"
                            :options="[
                                { value: 'rain', text: 'Rain' },
                                { value: 'heat', text: 'Heat' },
                                { value: 'fog', text: 'Fog / Visibility' },
                                { value: 'storm', text: 'Storm / Wind' },
                                { value: 'custom', text: 'Custom' },
                            ]"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Status" label-for="weatherStatus">
                        <b-form-select
                            id="weatherStatus"
                            v-model="weatherForm.active"
                            :options="[
                                { value: true, text: 'Active' },
                                { value: false, text: 'Inactive' },
                            ]"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Rule Name" label-for="weatherTitle">
                        <b-form-input
                            id="weatherTitle"
                            v-model="weatherForm.title"
                            placeholder="e.g. Heavy Rain"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Applies To" label-for="weatherApplies">
                        <b-form-select
                            id="weatherApplies"
                            v-model="weatherForm.appliesTo"
                            :options="[
                                { value: 'Both', text: 'Both (Ride & Parcel)' },
                                { value: 'Ride', text: 'Ride' },
                                { value: 'Parcel', text: 'Parcel' },
                            ]"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group
                        label="Condition"
                        label-for="weatherCondition"
                    >
                        <b-form-input
                            id="weatherCondition"
                            v-model="weatherForm.condition"
                            placeholder="e.g. Visibility < 100m"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group
                        label="Multiplier"
                        label-for="weatherMultiplier"
                    >
                        <b-form-input
                            id="weatherMultiplier"
                            type="number"
                            step="0.1"
                            v-model.number="weatherForm.multiplier"
                            placeholder="e.g. 1.3"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
            <div class="modal-actions mt-3">
                <b-button variant="secondary" @click="closeWeatherModal"
                    >Cancel</b-button
                >
                <b-button variant="dark" @click="saveWeatherRule">
                    {{ weatherEditIndex >= 0 ? "Save" : "Add" }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";

export default {
    name: "PricingAndFareEngine",
    components: {
        ComponentHeader,
    },
    data() {
        return {
            tabIndex: 0,
            editOpen: false,
            editIndex: -1,
            editTitle: "",
            editSub: "",
            editAmount: null,
            editSuffix: "",
            addOpen: false,
            newRuleTitle: "",
            newRuleSub: "",
            newRuleAmount: null,
            newRuleSuffix: "",

            surgeRules: [
                {
                    id: "sr-1",
                    zone: "Koramangala",
                    timePeriod: "Peak Hours (8-10 AM)",
                    serviceType: "Both",
                    multiplier: 1.8,
                },
                {
                    id: "sr-2",
                    zone: "Whitefield",
                    timePeriod: "Peak Hours (6-9 PM)",
                    serviceType: "Both",
                    multiplier: 2,
                },
                {
                    id: "sr-3",
                    zone: "MG Road",
                    timePeriod: "Lunch (12-2 PM)",
                    serviceType: "Ride",
                    multiplier: 1.5,
                },
                {
                    id: "sr-4",
                    zone: "Indiranagar",
                    timePeriod: "Evening (5-8 PM)",
                    serviceType: "Both",
                    multiplier: 1.6,
                },
                {
                    id: "sr-5",
                    zone: "Electronic City",
                    timePeriod: "Morning (7-10 AM)",
                    serviceType: "Parcel",
                    multiplier: 1.7,
                },
            ],
            surgeSettings: {
                highDemandThreshold: 80,
                maxMultiplier: 3.0,
            },
            surgeModalOpen: false,
            surgeEditIndex: -1,
            surgeForm: {
                zone: "",
                timePeriod: "",
                serviceType: "Both",
                multiplier: 1.8,
            },

            weatherRules: [
                {
                    id: "wr-1",
                    kind: "rain",
                    title: "Heavy Rain",
                    condition: "",
                    multiplier: 1.3,
                    appliesTo: "Ride",
                    active: true,
                },
                {
                    id: "wr-2",
                    kind: "heat",
                    title: "Extreme Heat",
                    condition: "Above 40°C",
                    multiplier: 1.2,
                    appliesTo: "Both",
                    active: true,
                },
                {
                    id: "wr-3",
                    kind: "fog",
                    title: "Fog / Poor Visibility",
                    condition: "Visibility < 100m",
                    multiplier: 1.4,
                    appliesTo: "Both",
                    active: true,
                },
                {
                    id: "wr-4",
                    kind: "storm",
                    title: "Storm / High Winds",
                    condition: "Wind speed > 50 km/h",
                    multiplier: 1.5,
                    appliesTo: "Both",
                    active: false,
                },
            ],
            weatherModalOpen: false,
            weatherEditIndex: -1,
            weatherForm: {
                kind: "rain",
                title: "",
                condition: "",
                appliesTo: "Both",
                multiplier: 1.2,
                active: true,
            },

            pricingRules: [
                {
                    key: "baseFare",
                    title: "Base Fare",
                    sub: "Initial charge per trip",
                    amount: 40,
                    suffix: "",
                },
                {
                    key: "perKm",
                    title: "Per KM",
                    sub: "Distance charge",
                    amount: 12,
                    suffix: "",
                },
                {
                    key: "perMinute",
                    title: "Per Minute",
                    sub: "Time charge",
                    amount: 2,
                    suffix: "",
                },
                {
                    key: "waitingCharge",
                    title: "Waiting Charge",
                    sub: "After 3 minutes",
                    amount: 3,
                    suffix: "/min",
                },
                {
                    key: "cancellationFee",
                    title: "Cancellation Fee",
                    sub: "After driver assignment",
                    amount: 50,
                    suffix: "",
                },
                {
                    key: "bookingFee",
                    title: "Booking Fee",
                    sub: "Platform fee",
                    amount: 10,
                    suffix: "",
                },
            ],
            surgeFields: [
                { key: "zone", label: "ZONE" },
                { key: "timePeriod", label: "TIME PERIOD" },
                { key: "serviceType", label: "SERVICE TYPE" },
                { key: "multiplier", label: "MULTIPLIER" },
                { key: "actions", label: "ACTIONS", class: "text-center" },
            ],
        };
    },
    methods: {
        // Helper methods
        sanitizeString(value, defaultValue = "") {
            return (value || "").trim() || defaultValue;
        },
        validateNumber(value, defaultValue = 0) {
            return typeof value === "number" && !Number.isNaN(value)
                ? value
                : defaultValue;
        },
        generateKey(prefix, title = "rule") {
            const safeKeyBase = (title || "rule")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "");
            return `${safeKeyBase}-${Date.now()}`;
        },
        generateId(prefix) {
            return `${prefix}-${Date.now()}`;
        },

        // Pricing Rules methods
        exportConfig() {
            // Static button (no export logic yet)
            return;
        },
        openEdit(rule) {
            this.addOpen = false;
            const idx = this.pricingRules.findIndex((r) => r.key === rule.key);
            this.editIndex = idx;
            this.editTitle = rule?.title || "";
            this.editSub = rule?.sub || "";
            this.editAmount =
                rule && typeof rule.amount !== "undefined" ? rule.amount : null;
            this.editSuffix = rule?.suffix || "";
            this.editOpen = true;
        },
        closeEdit() {
            this.editOpen = false;
            this.editIndex = -1;
            this.editTitle = "";
            this.editSub = "";
            this.editAmount = null;
            this.editSuffix = "";
        },
        saveEdit() {
            if (
                this.editIndex >= 0 &&
                this.editIndex < this.pricingRules.length
            ) {
                const updates = {
                    title: this.sanitizeString(this.editTitle, "Untitled"),
                    sub: this.sanitizeString(this.editSub, "—"),
                    amount: this.validateNumber(this.editAmount, 0),
                    suffix: this.sanitizeString(this.editSuffix),
                };
                Object.keys(updates).forEach((key) => {
                    this.$set(
                        this.pricingRules[this.editIndex],
                        key,
                        updates[key],
                    );
                });
            }
            this.closeEdit();
        },
        openAdd() {
            this.editOpen = false;
            this.resetAddForm();
            this.addOpen = true;
        },
        resetAddForm() {
            this.newRuleTitle = "";
            this.newRuleSub = "";
            this.newRuleAmount = null;
            this.newRuleSuffix = "";
        },
        closeAdd() {
            this.addOpen = false;
            this.resetAddForm();
        },
        saveAdd() {
            const title = this.sanitizeString(this.newRuleTitle);
            const sub = this.sanitizeString(this.newRuleSub);
            const amount = this.validateNumber(this.newRuleAmount, 0);
            const suffix = this.sanitizeString(this.newRuleSuffix);
            const key = this.generateKey("rule", title);

            this.pricingRules.unshift({
                key,
                title: title || "New Rule",
                sub: sub || "Custom pricing rule",
                amount,
                suffix,
            });

            this.closeAdd();
        },

        // Utility methods
        serviceTypeClass(type) {
            const typeMap = {
                Both: "service-both",
                Ride: "service-ride",
                Parcel: "service-parcel",
            };
            return typeMap[type] || "service-both";
        },
        formatMultiplier(multiplier) {
            const num = Number(multiplier);
            return Number.isNaN(num) ? "—" : `${num}x`;
        },
        weatherIcon(kind) {
            const iconMap = {
                rain: "fa-tint",
                heat: "fa-sun-o",
                fog: "fa-cloud",
                storm: "fa-bolt",
            };
            return iconMap[kind] || "fa-cloud";
        },
        getAppliesToLabel(appliesTo) {
            const labelMap = {
                Ride: "Rides",
                Parcel: "Parcel",
            };
            return labelMap[appliesTo] || "Both";
        },
        weatherSubtitle(rule) {
            if (!rule) return "";
            const condition = this.sanitizeString(rule.condition);
            const appliesTo = this.getAppliesToLabel(rule.appliesTo);
            const parts = [];
            if (condition) parts.push(condition);
            parts.push(`Multiplier: ${this.formatMultiplier(rule.multiplier)}`);
            parts.push(`Applies to: ${appliesTo}`);
            return parts.join(" • ");
        },

        // Weather Rules methods
        resetWeatherForm() {
            this.weatherForm = {
                kind: "rain",
                title: "",
                condition: "",
                appliesTo: "Both",
                multiplier: 1.2,
                active: true,
            };
        },
        openWeatherCreate() {
            this.weatherEditIndex = -1;
            this.resetWeatherForm();
            this.weatherModalOpen = true;
        },
        openWeatherEdit(idx) {
            const row = this.weatherRules[idx];
            if (!row) return;
            this.weatherEditIndex = idx;
            this.weatherForm = {
                kind: row.kind || "custom",
                title: row.title || "",
                condition: row.condition || "",
                appliesTo: row.appliesTo || "Both",
                multiplier: row.multiplier,
                active: typeof row.active === "boolean" ? row.active : true,
            };
            this.weatherModalOpen = true;
        },
        closeWeatherModal() {
            this.weatherModalOpen = false;
            this.weatherEditIndex = -1;
        },
        saveWeatherRule() {
            const existingId =
                this.weatherEditIndex >= 0 &&
                this.weatherEditIndex < this.weatherRules.length &&
                this.weatherRules[this.weatherEditIndex]
                    ? this.weatherRules[this.weatherEditIndex].id
                    : null;

            const payload = {
                id: existingId || this.generateId("wr"),
                kind: this.weatherForm.kind || "custom",
                title: this.sanitizeString(this.weatherForm.title, "Untitled"),
                condition: this.sanitizeString(this.weatherForm.condition),
                appliesTo: this.weatherForm.appliesTo || "Both",
                multiplier: this.validateNumber(this.weatherForm.multiplier, 1),
                active: Boolean(this.weatherForm.active),
            };

            if (this.weatherEditIndex >= 0) {
                this.$set(this.weatherRules, this.weatherEditIndex, payload);
            } else {
                this.weatherRules.unshift(payload);
            }

            this.closeWeatherModal();
        },

        // Surge Rules methods
        resetSurgeForm() {
            this.surgeForm = {
                zone: "",
                timePeriod: "",
                serviceType: "Both",
                multiplier: 1.8,
            };
        },
        openSurgeCreate() {
            this.surgeEditIndex = -1;
            this.resetSurgeForm();
            this.surgeModalOpen = true;
        },
        openSurgeEdit(idx) {
            const row = this.surgeRules[idx];
            if (!row) return;
            this.surgeEditIndex = idx;
            this.surgeForm = {
                zone: row.zone,
                timePeriod: row.timePeriod,
                serviceType: row.serviceType,
                multiplier: row.multiplier,
            };
            this.surgeModalOpen = true;
        },
        closeSurgeModal() {
            this.surgeModalOpen = false;
            this.surgeEditIndex = -1;
        },
        saveSurgeRule() {
            const existingId =
                this.surgeEditIndex >= 0 &&
                this.surgeEditIndex < this.surgeRules.length &&
                this.surgeRules[this.surgeEditIndex]
                    ? this.surgeRules[this.surgeEditIndex].id
                    : null;

            const payload = {
                id: existingId || this.generateId("sr"),
                zone: this.sanitizeString(this.surgeForm.zone, "—"),
                timePeriod: this.sanitizeString(this.surgeForm.timePeriod, "—"),
                serviceType: this.surgeForm.serviceType || "Both",
                multiplier: this.validateNumber(this.surgeForm.multiplier, 1),
            };

            if (this.surgeEditIndex >= 0) {
                this.$set(this.surgeRules, this.surgeEditIndex, payload);
            } else {
                this.surgeRules.unshift(payload);
            }

            this.closeSurgeModal();
        },
        deleteSurgeRule(idx) {
            this.surgeRules.splice(idx, 1);
        },
        saveSurgeSettings() {
            // Static save action placeholder
            return;
        },
    },
};
</script>

<style scoped>
.pricing-page {
    padding: 8px 8px 16px 16px;
}

/* Tabs - all three in same row, all fully visible */
.pricing-tabs >>> .nav-tabs {
    border: 1px solid var(--slate-200);
    border-radius: 10px;
    background: var(--slate-100);
    padding: 6px 6px;
    column-gap: 8px;
    display: flex !important;
    flex-wrap: nowrap !important;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.pricing-tabs >>> .nav-tabs .nav-item {
    flex: 1 1 0;
    min-width: 95px;
    margin: 0;
}

.pricing-tabs >>> .nav-tabs .nav-link {
    border-radius: 10px;
    border: 1px solid transparent;
    font-weight: 700;
    font-size: 11px;
    color: var(--slate-800);
    padding: 8px 6px;
    white-space: nowrap;
    width: 100%;
    text-align: center;
    display: block;
    background: transparent;
}

.pricing-tabs >>> .nav-tabs .nav-link:hover {
    background: rgba(255, 255, 255, 0.6);
}

.pricing-tabs >>> .nav-tabs .nav-link.active {
    background: #fff;
    border-color: var(--slate-200);
    box-shadow: var(--shadow-sm);
}

.service-mode-card >>> .card-body {
    width: 100%;
    padding: 12px 16px;
}

.service-mode-row {
    width: 100%;
    justify-content: center;
}

.service-mode-col {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.service-mode-label {
    font-size: 12px;
    color: var(--slate-600);
    font-weight: 800;
}

.service-mode-btn {
    margin-left: 0;
}

.mode-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.rule-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
}

.pricing-header-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.pricing-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--slate-800);
}

.add-rule-btn {
    height: 32px;
    padding: 0 12px;
    font-size: 12px;
    flex-shrink: 0;
    white-space: nowrap;
    margin-left: auto;
}

.rule-card {
    border: 1px solid var(--slate-200);
    border-radius: 12px;
    padding: 14px 14px 12px;
}

.rule-card >>> .card-body {
    padding: 0;
}

.rule-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.rule-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--slate-800);
}

.rule-sub {
    font-size: 13px;
    color: var(--slate-500);
    font-weight: 500;
    margin-top: 4px;
}

.rule-icon {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
    background: #fff;
    color: var(--slate-400);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
}

.rule-icon i {
    font-size: 14px;
}

.rule-value {
    margin-top: 10px;
    font-size: 28px;
    font-weight: 700;
    color: var(--secondary);
}

.currency {
    margin-right: 2px;
}

.suffix {
    margin-left: 2px;
    font-size: 16px;
    font-weight: 900;
}

.rule-edit-btn {
    width: 100%;
    height: 40px;
    margin-top: 12px;
    border-radius: 12px;
    border: 1px solid var(--slate-200);
    background: #fff;
    color: var(--slate-800);
    font-size: 13px;
    font-weight: 900;
}

.rule-edit-btn:hover {
    background: var(--slate-50);
}

.modal-subtitle {
    font-size: 12px;
    color: var(--slate-500);
    margin-bottom: 1rem;
}

.modal-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 14px;
    margin-top: 8px;
}

.modal-grid .modal-label {
    margin-top: 0;
}

.modal-label {
    display: block;
    margin-top: 12px;
    font-size: 12px;
    font-weight: 800;
    color: var(--slate-700);
}

.modal-input {
    width: 100%;
    height: 44px;
    margin-top: 1px;
    border-radius: 12px;
    border: 1px solid var(--slate-200);
    padding: 0 12px;
    outline: none;
    color: var(--slate-800);
    font-weight: 700;
}

.money-input {
    display: grid;
    grid-template-columns: 36px 1fr 160px;
    gap: 8px;
    align-items: center;
    margin-top: 1px;
}

.money-prefix {
    height: 44px;
    border-radius: 12px;
    border: 1px solid var(--slate-200);
    background: var(--slate-50);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    color: var(--slate-800);
}

.money-field {
    margin-top: 0;
}

.money-suffix-input {
    margin-top: 0;
}

.modal-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 14px;
}

.modal-btn {
    height: 40px;
    padding: 0 14px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 900;
    border: 1px solid var(--slate-200);
}

.modal-btn.cancel {
    background: #fff;
    color: var(--slate-800);
}

.modal-btn.cancel:hover {
    background: var(--slate-50);
}

.modal-btn.save {
    background: #0f172a;
    border-color: #0f172a;
    color: #fff;
}

.modal-btn.save:hover {
    background: #111827;
    border-color: #111827;
}

/* SURGE PRICING */
.surge-card >>> .card-body {
    overflow: visible;
}

.surge-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.surge-header-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.surge-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--slate-800);
}

.surge-city {
    font-weight: 700;
}

.create-surge-btn {
    height: 32px;
    padding: 0 12px;
    font-size: 12px;
    flex-shrink: 0;
    white-space: nowrap;
    margin-left: auto;
}

.surge-table {
    width: 100%;
    border-collapse: collapse;
}

.zone-cell,
.time-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.zone-icon,
.time-icon {
    color: var(--slate-400);
}

.zone-name {
    font-weight: 600;
}

.service-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
}

.service-both {
    background: rgba(139, 92, 246, 0.12);
    color: #7c3aed;
}

.service-ride {
    background: rgba(59, 130, 246, 0.12);
    color: #2563eb;
}

.service-parcel {
    background: rgba(16, 185, 129, 0.12);
    color: #059669;
}

.multiplier {
    color: #ff4d00;
    font-weight: 600;
}

.actions-cell {
    display: inline-flex;
    align-items: center;
    gap: 12px;
}

.action-edit {
    height: 28px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
    background: #fff;
    color: #000;
    font-weight: 900;
    font-size: 12px;
}

.action-edit:hover {
    background: var(--slate-50);
}

.action-delete {
    padding: 0;
    border: 0;
    background: transparent;
    color: #ff0000;
    font-weight: 900;
    font-size: 12px;
}

.action-delete:hover {
    text-decoration: underline;
}

.inline_btnTable {
    display: flex;
    justify-content: center;
    gap: 6px;
}

.button-circle {
    width: 34px;
    height: 34px;
    border-radius: 50% !important;
    padding: 0 !important;
    margin: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.button-circle i {
    margin: 0;
    font-size: 14px;
    line-height: 1;
}

.btn-orange {
    background: #fe7b08 !important;
    border: #fe7b08 !important;
}

.button-circle.btn-orange i {
    color: white !important;
}

.settings-title {
    font-size: 13px;
    font-weight: 900;
    color: var(--slate-800);
    margin-bottom: 10px;
}

.settings-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.setting-label {
    font-size: 11px;
    font-weight: 900;
    color: #000;
}

.setting-input {
    width: 100%;
    height: 34px;
    margin-top: 6px;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
    background: var(--slate-50);
    padding: 0 10px;
    outline: none;
    font-weight: 800;
    color: var(--slate-800);
}

.setting-help {
    margin-top: 6px;
    font-size: 10px;
    color: var(--slate-500);
    font-weight: 600;
}

.save-settings-btn {
    height: 34px;
}

.surge-submit-btn {
    width: 100%;
    height: 40px;
    margin-top: 14px;
    border-radius: 12px;
    background: #0f172a;
    border: 1px solid #0f172a;
    color: #fff;
    font-weight: 900;
}

.surge-submit-btn:hover {
    background: #111827;
    border-color: #111827;
}

/* WEATHER PRICING */
.weather-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.weather-header-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.weather-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--slate-800);
}

.add-weather-btn {
    height: 32px;
    padding: 0 12px;
    font-size: 12px;
    flex-shrink: 0;
    white-space: nowrap;
    margin-left: auto;
}

.weather-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.weather-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px;
    border: 1px solid var(--slate-200);
    border-radius: 12px;
    background: #fff;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.weather-item:hover {
    border-color: var(--slate-300);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.weather-left {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex: 1;
}

.weather-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
}

.weather-icon i {
    font-size: 20px;
}

.weather-rain {
    background: rgba(59, 130, 246, 0.15);
    color: #2563eb;
}

.weather-heat {
    background: rgba(249, 115, 22, 0.16);
    color: #ea580c;
}

.weather-fog {
    background: rgba(100, 116, 139, 0.15);
    color: var(--slate-700);
}

.weather-storm {
    background: rgba(168, 85, 247, 0.15);
    color: #7c3aed;
}

.weather-custom {
    background: var(--slate-100);
    color: var(--slate-700);
}

.weather-text {
    min-width: 0;
}

.weather-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--slate-800);
}

.weather-desc {
    margin-top: 4px;
    font-size: 12px;
    color: var(--slate-600);
    font-weight: 500;
    line-height: 1.4;
}

.weather-right {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

@media (max-width: 575.98px) {
    .weather-item {
        flex-wrap: wrap;
    }
    .weather-right {
        width: 100%;
        justify-content: flex-end;
    }
}

.weather-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 5px 12px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
}

.weather-status.is-active {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
}

.weather-status.is-inactive {
    background: var(--slate-100);
    color: var(--slate-600);
}

.weather-edit-btn {
    padding: 0;
    border: 0;
    background: transparent;
    color: #000;
    font-weight: 900;
    font-size: 12px;
}

.weather-edit-btn:hover {
    text-decoration: underline;
}

.weather-empty {
    padding: 24px;
    border: 1px dashed var(--slate-200);
    border-radius: 12px;
    color: var(--slate-500);
    font-weight: 600;
    font-size: 13px;
    text-align: center;
}

@media (max-width: 991.98px) {
    .settings-grid {
        grid-template-columns: 1fr;
    }
}

.formula-title {
    font-size: 12px;
    font-weight: 900;
    color: var(--slate-800);
    margin-bottom: 8px;
}

.formula-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.formula-mono {
    font-family:
        ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
        "Liberation Mono", "Courier New", monospace;
    font-size: 10px;
    color: var(--slate-800);
    line-height: 1.6;
}

.formula-bullets {
    margin-top: 6px;
}

.example-box {
    border: 1px solid rgba(59, 130, 246, 0.25);
    background: rgba(59, 130, 246, 0.08);
    border-radius: 6px;
    padding: 10px;
}

.example-title {
    font-size: 10px;
    font-weight: 900;
    color: var(--slate-800);
    margin-bottom: 6px;
}

.example-lines {
    font-family:
        ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
        "Liberation Mono", "Courier New", monospace;
    font-size: 10px;
    color: var(--slate-800);
    line-height: 1.6;
}

.example-total {
    margin-top: 6px;
    font-weight: 900;
}

.example-note {
    margin-top: 6px;
    color: var(--slate-500);
    font-weight: 700;
}

.placeholder-title {
    font-size: 14px;
    font-weight: 900;
    color: var(--slate-800);
}

.placeholder-sub {
    margin-top: 6px;
    font-size: 12px;
    color: var(--slate-500);
    font-weight: 600;
}

@media (max-width: 991.98px) {
    .rule-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 575.98px) {
    .rule-grid {
        grid-template-columns: 1fr;
    }

    .modal-grid {
        grid-template-columns: 1fr;
    }
}
</style>
